/**
 * The small pieces Time (Board 7) and Money (Board 8) both draw — the
 * *Internal* chip, the locked total, a difference in its tone — and the two
 * conversions a punch's popup needs between an instant and a minute of the
 * visit's own day.
 *
 * The *Internal* chip itself is Slice 1's (`internal-chip.tsx`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type { Delta, IsoDay, IsoMoment } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { EM_DASH } from '../lib/format.js'
import { Popover } from '../components/overlays.js'
import { VisitGlyph } from './glyphs.js'

/** The lock alone, in the chip's purple — before a locked total (Board 7's day header). */
export function InternalLock({ size }: { size: number }) {
  return (
    <span className="inline-flex shrink-0" style={{ color: 'rgb(var(--status-kind-fee-fg))' }}>
      <VisitGlyph name="lock" size={size} stroke={2.4} />
    </span>
  )
}

/** A day's locked total — *🔒 $262.07* (Board 7): 24 inside its border, as the board's box draws it, on `surface-internal`. */
export function LockedTotal({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-[7px] border border-line bg-surface-internal
                     px-[9px] text-[12px] font-bold">
      <InternalLock size={11} />
      {children}
    </span>
  )
}

/** The text colour of a difference (§3.2): amber over, green under, faint at nought. */
export const DELTA_TEXT: Record<Delta['tone'], string> = {
  over: 'text-warn',
  under: 'text-accent-dark',
  zero: 'text-text-faint',
}

/**
 * A difference printed bold in its tone — *+$34.80* amber, *−$41.25* green, a
 * faint *—* at nought (§3.2). `text` is what the board prints for it.
 */
export function DeltaText({ delta, text, className }: { delta: Delta; text: string; className?: string }) {
  if (delta.tone === 'zero') return <span className={cn('text-text-faint', className)}>{EM_DASH}</span>
  return <span className={cn('whitespace-nowrap font-bold', DELTA_TEXT[delta.tone], className)}>{text}</span>
}

/**
 * A difference as the small tag beside a big number — *+$19.47*, *−0.2 pts*
 * (Board 8's cards), *+1.0 h · old footing* (Board 7's day header): 19 high,
 * radius 5, 11/700.
 */
export function DeltaTag({ tone, children }: { tone: Delta['tone']; children: ReactNode }) {
  return (
    <span
      className={cn('inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-bold',
        tone === 'under' ? 'bg-accent-soft text-accent-dark' : tone === 'over' ? 'bg-warn-bg text-warn' : 'bg-line-soft text-text-faint')}
    >
      {children}
    </span>
  )
}

/** A card's uppercase label — *ON THIS VISIT*, *PRICE* (Boards 7, 8): 10.5/600, 0.06em, faint. */
export function KpiLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{children}</span>
  )
}

// ---------------------------------------------------------------------------
// An instant and a minute of the visit's day
// ---------------------------------------------------------------------------

/** Minutes from local midnight, in the visit's zone — where a punch sits on the 6 AM – 6 PM bar. */
export function minuteOfDay(timeZone: string, at: IsoMoment): number {
  const date = new Date(at)
  return Number(formatInTimeZone(date, timeZone, 'H')) * 60 + Number(formatInTimeZone(date, timeZone, 'm'))
}

/** The instant a minute of a calendar day is, in the visit's zone. */
export function instantAt(timeZone: string, day: IsoDay, minute: number): IsoMoment {
  const hh = String(Math.floor(minute / 60)).padStart(2, '0')
  const mm = String(minute % 60).padStart(2, '0')
  return fromZonedTime(`${day} ${hh}:${mm}:00`, timeZone).toISOString()
}

/** A minute of the day as a time box shows it — 750 → *12:30 PM*. */
export function clockOf(minute: number): string {
  const hours = Math.floor(minute / 60)
  const mm = String(minute % 60).padStart(2, '0')
  const hour = hours % 12 || 12
  return `${hour}:${mm} ${hours < 12 ? 'AM' : 'PM'}`
}

/**
 * What is typed in a time box, as a minute of the day — *12:30 PM*, *12:30p*,
 * *1230*, *7*. Without an AM or PM, a crew's day decides: 6 – 11 is the
 * morning and 12 – 5 the afternoon (the bar runs 6 AM to 6 PM). Null when it
 * is not a time.
 */
export function parseClock(text: string): number | null {
  const match = /^\s*(\d{1,2})(?:[:.]?(\d{2}))?\s*([ap])?\.?\s*m?\.?\s*$/i.exec(text)
  if (!match) return null
  let hour = Number(match[1])
  const minute = Number(match[2] ?? '0')
  if (minute > 59) return null
  const meridiem = match[3]?.toLowerCase()
  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === 'p' && hour < 12) hour += 12
    if (meridiem === 'a' && hour === 12) hour = 0
  } else if (hour > 23) {
    return null
  } else if (hour >= 1 && hour <= 5) {
    hour += 12
  }
  return hour * 60 + minute
}

// ---------------------------------------------------------------------------
// The width a tab is given
// ---------------------------------------------------------------------------

/**
 * The width of the box a tab draws in — measured, never the window's (§19.1:
 * *cards measured by the panel*), so the iPad's 820 column and a desktop's
 * 1330 one each get the layout that fits them.
 */
export function useBoxWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const read = () => setWidth(node.getBoundingClientRect().width)
    read()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(read)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return [ref, width]
}

// ---------------------------------------------------------------------------
// A field that picks one of a few
// ---------------------------------------------------------------------------

export interface Choice<T extends string> {
  value: T
  label: string
  /** Drawn before the label — a face for a person. */
  lead?: ReactNode
}

/**
 * A label above a box that picks one of a few — *Punch*, *Person*, *Day*
 * (Boards 7 B, 18 E). On a desktop the box opens a menu under it; on a phone
 * the choices are chips in place, because a phone's choice is never a
 * drop-down (`build-exactly-as-drawn.md` law 6).
 */
export function ChoiceField<T extends string>({
  label, required = false, value, choices, onChange, phone, height = 36, chevron = true, help, layer,
}: {
  label: string
  required?: boolean
  value: T | null
  choices: Choice<T>[]
  onChange: (value: T) => void
  phone: boolean
  /** 36 for Board 7 B and 18 E's top row, 34 for 18 E's times. */
  height?: 34 | 36
  /** Board 18 E's *Day* is drawn without one. */
  chevron?: boolean
  help?: ReactNode
  layer: number
}) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const chosen = choices.find((choice) => choice.value === value) ?? null
  return (
    <div className="min-w-0">
      <FieldLabel required={required}>{label}</FieldLabel>
      {phone ? (
        <div className="flex flex-wrap gap-2">
          {choices.map((choice) => (
            <button
              key={choice.value}
              type="button"
              onClick={() => onChange(choice.value)}
              aria-pressed={choice.value === value}
              className={cn('inline-flex min-h-11 items-center gap-2 rounded-full border px-3.5 text-[13.5px] font-semibold',
                choice.value === value ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text')}
            >
              {choice.lead}
              {choice.label}
            </button>
          ))}
        </div>
      ) : (
        <>
          <button
            ref={setAnchor}
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-haspopup="listbox"
            aria-expanded={open}
            className="flex w-full min-w-0 items-center gap-2 rounded-button border border-line bg-surface px-[11px] text-left
                       text-[13px] text-text hover:border-line-strong"
            style={{ height }}
          >
            {chosen?.lead}
            <span className="min-w-0 flex-1 truncate">{chosen?.label ?? ''}</span>
            {chevron && <span className="text-text-muted"><VisitGlyph name="chevronDown" size={13} stroke={2} /></span>}
          </button>
          <Popover open={open} onClose={() => setOpen(false)} label={label} anchor={anchor}
            width={Math.max(anchor?.offsetWidth ?? 0, 200)} layer={layer} className="py-1">
            <div role="listbox" aria-label={label}>
              {choices.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  role="option"
                  aria-selected={choice.value === value}
                  onClick={() => { onChange(choice.value); setOpen(false) }}
                  className={cn('flex h-9 w-full items-center gap-2 px-3 text-left text-[13px]',
                    choice.value === value ? 'bg-accent-soft font-semibold text-accent-dark' : 'text-text hover:bg-hover')}
                >
                  {choice.lead}
                  <span className="truncate">{choice.label}</span>
                </button>
              ))}
            </div>
          </Popover>
        </>
      )}
      {help}
    </div>
  )
}

/** A field's label — 11.5 muted, 5 above the box, and the green dot when it is required (Boards 7 B, 18 E). */
export function FieldLabel({ children, required = false, htmlFor }: { children: ReactNode; required?: boolean; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
      {children}
      {required && <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
    </label>
  )
}

/**
 * A time typed into a box — *12:30 PM* (Board 7 B's *Time*, Board 18 E's
 * six). Read as the person types; written back as the board prints it once
 * they leave the box.
 */
export function TimeBox({
  id, label, value, onChange, height = 34, autoFocus = false, phone = false,
}: {
  id: string
  label: string
  value: string
  onChange: (text: string) => void
  height?: 34 | 36
  autoFocus?: boolean
  phone?: boolean
}) {
  return (
    <input
      id={id}
      aria-label={label}
      value={value}
      inputMode="text"
      autoComplete="off"
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
      onBlur={() => {
        const minute = parseClock(value)
        if (minute !== null) onChange(clockOf(minute))
      }}
      className={cn('block w-full min-w-0 rounded-button border border-line bg-surface px-[11px] tabular-nums text-text outline-none',
        'focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]',
        phone ? 'h-11 text-[15px]' : height === 36 ? 'text-[13px]' : 'text-[12.5px]')}
      style={phone ? undefined : { height }}
    />
  )
}
